import CloseSVG from '@public/icons/close-btn.svg';
import { useAnimation } from 'framer-motion';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import Constructor from '@/shared/components/constructor/Constructor';
import Popup from '@/shared/components/shared/Popup';
import Typografed from '@/shared/components/shared/Typografed';
import { useMounted } from '@/shared/hooks/use-mounted';
import { easeInOutSine } from '@/shared/lib/utils/easings';
import type { LiveProgramItem } from '../../sections/LiveProgramSection/LiveProgramSection';
import Image from '../../shared/Image';
import Pagination from '../../shared/Pagination/Pagination';
import Responsive from '../../shared/Responsive';
import { SCHEMA_POPUP_NAME } from '../SchemaPopup/SchemaPopup';

export const LIVE_PROGRAM_POPUP_NAME = 'live-program-popup';

function LiveProgramPopup({ item }: { item: LiveProgramItem | null }) {
    const { openPopup, closePopup } = useSetOpenedPopupsState();
    const [lastRealIndex, setLastRealIndex] = useState(0);
    const [currentRealIndex, setCurrentRealIndex] = useState(0);
    const numSlides = item?.images?.length ?? 0;
    const [direction, setDirection] = useState(0);
    const controls = useAnimation();
    const device = useMediaQueryDeviceStateValue();
    const isMobile = device.includes('mobile');
    const isMounted = useMounted();
    const sliderRef = useRef<SwiperRef>();

    useEffect(() => {
        if (currentRealIndex === 0 && lastRealIndex === numSlides - 1) {
            setDirection(1);
        } else if (currentRealIndex === numSlides - 1 && lastRealIndex === 0) {
            setDirection(-1);
        } else if (currentRealIndex > lastRealIndex) {
            setDirection(1);
        } else if (currentRealIndex < lastRealIndex) {
            setDirection(-1);
        }
        controls.start({
            x: direction > 0 ? [0, 30, 0] : [0, -30, 0],
            scale: [1.1, 1.1],
            transition: {
                duration: 0.9,
                ease: easeInOutSine,
                delay: 0.1,
                stiffness: 0,
                damping: 0,
            },
        });
    }, [currentRealIndex, lastRealIndex, numSlides, direction, controls]);

    const accessLabel = item?.access.join(', ') ?? '';

    return (
        <Popup
            name={LIVE_PROGRAM_POPUP_NAME}
            data-popup-preset="slide-right"
            className="live-program-popup"
            overlay={true}
        >
            <button
                className="menu-popup-close-btn"
                onClick={() => {
                    closePopup(LIVE_PROGRAM_POPUP_NAME);
                }}
                aria-label="Закрыть окно"
            >
                <CloseSVG />
            </button>
            <div className="live-program-popup-content">
                {(item?.title || item?.prefix) && (
                    <div className="live-program-popup-content__top">
                        {item?.title && (
                            <div className="live-program-popup-content__title text-xl">
                                <Typografed>{item.title}</Typografed>
                            </div>
                        )}
                        {item?.prefix && (
                            <div className="live-program-popup-content__prefix text-xs text-opacity">
                                <Typografed>{item.prefix}</Typografed>
                            </div>
                        )}
                    </div>
                )}
                <div className="live-program-popup-content__info">
                    {item?.datetime && (
                        <div className="live-program-popup-content__info-item">
                            <p className="live-program-popup-content__info-label text-xs">Дата и время:</p>
                            <p className="live-program-popup-content__info-value text-s">
                                <Typografed>{item.datetime}</Typografed>
                            </p>
                        </div>
                    )}
                    {accessLabel && (
                        <div className="live-program-popup-content__info-item">
                            <p className="live-program-popup-content__info-label text-xs">Доступ:</p>
                            <p className="live-program-popup-content__info-value text-s">
                                <Typografed>{accessLabel}</Typografed>
                            </p>
                        </div>
                    )}
                    {item?.place && (
                        <div className="live-program-popup-content__info-item">
                            <p className="live-program-popup-content__info-label text-xs">Место:</p>
                            <div
                                role="button"
                                tabIndex={0}
                                className={`live-program-popup-content__info-value live-program-popup-content__info-schema text-s ${
                                    item.place.image?.src ? '' : 'live-program-popup-content__info-value--disabled'
                                }`}
                                onClick={() => {
                                    if (item.place?.image?.src) {
                                        openPopup(SCHEMA_POPUP_NAME);
                                    }
                                }}
                                aria-label="Открыть схему"
                            >
                                <Typografed>{item.place.name}</Typografed>
                            </div>
                        </div>
                    )}
                    {item?.tags && item.tags.length > 0 && (
                        <div className="live-program-popup-content__info-item">
                            <p className="live-program-popup-content__info-label text-xs">Тематика:</p>
                            <ul className="live-program-popup-content__tags list-unstyled">
                                {item.tags.map((tag) => (
                                    <li key={tag} className="live-program-popup-content__tag text-xs">
                                        <Typografed>{tag}</Typografed>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {item?.description && (
                    <div
                        className="live-program-popup-content__description text-s wysiwyg"
                        dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                )}
                {item?.content && item.content.length > 0 && (
                    <Constructor blocks={item.content} className="live-program-popup-content__constructor" />
                )}
                {item?.images?.length ? (
                    <div className="live-program-popup-content__slider">
                        {numSlides > 1 && isMounted ? (
                            <div className="live-program-popup-content__slider-wrapper">
                                <Swiper
                                    key={item.id}
                                    className="how-it-was-slider"
                                    modules={[Navigation]}
                                    navigation={{
                                        prevEl: '.slider-prev-btn',
                                        nextEl: '.slider-next-btn',
                                    }}
                                    ref={sliderRef as MutableRefObject<SwiperRef>}
                                    speed={650}
                                    slidesPerView={1}
                                    spaceBetween={20}
                                    centeredSlides={isMobile}
                                    onSlideChange={(swiper) => {
                                        const newRealIndex = swiper.realIndex;
                                        setLastRealIndex(currentRealIndex);
                                        setCurrentRealIndex(newRealIndex);
                                    }}
                                    onResize={(swiper) => swiper.update()}
                                >
                                    {item.images.map((slide, i) => (
                                        <SwiperSlide className="live-program-popup-content__slide" key={i}>
                                            <Responsive className="how-it-was-slider-img">
                                                <div className="how-it-was-slider-img-inner">
                                                    <Image
                                                        className="responsive__item"
                                                        src={slide.src}
                                                        alt={slide.alt || ''}
                                                        title={slide.title}
                                                        width={slide.width ?? 640}
                                                        height={slide.height ?? 400}
                                                        sizes="(max-width: 768px) 98vw, 40vw"
                                                    />
                                                </div>
                                            </Responsive>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                                <Pagination
                                    numSlides={numSlides}
                                    currentRealIndex={currentRealIndex}
                                    lastRealIndex={lastRealIndex}
                                    prevElClass="slider-prev-btn"
                                    nextElClass="slider-next-btn"
                                    wrapperClass="pagination-wrapper wrapper"
                                />
                            </div>
                        ) : (
                            <div className="wrapper swiper__wrapper">
                                {item.images.map((slide, i) => (
                                    <div className="swiper-slide reviews-slider-slide" key={i}>
                                        <div className="reviews-slider-slide-inner">
                                            <Responsive className="reviews-slider-img-wrapper">
                                                <Image
                                                    className="reviews-slider-img"
                                                    src={slide.src}
                                                    alt={slide.alt || ''}
                                                    title={slide.title}
                                                    width={slide.width ?? 640}
                                                    height={slide.height ?? 400}
                                                    sizes="(max-width: 1199px) 20vw, 30vw"
                                                />
                                            </Responsive>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
        </Popup>
    );
}

export default LiveProgramPopup;
